import { useRef, useState, useEffect } from 'react';
import NoticeToast from '../common/NoticeToast';
import { useNotice } from '../../hooks/useNotice';
import './ValidationInterface.css';

const getTodayIsoDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const GUIDE = {
  "Évaluation de la totalité des compétences du cours": {
    Excellent: "L’ensemble des évaluations sommatives couvre la totalité des éléments de compétences visées...",
    Bien: "L’ensemble des évaluations sommatives couvre plusieurs éléments de compétences, mais de manière incomplète...",
    Insuffisant: "Seulement une partie limitée est évaluée, laissant des lacunes.",
  },
  "Distribution équilibrée des évaluations sommatives": {
    Excellent: "Charge de travail équilibrée et réaliste, temps de réalisation réaliste.",
    Bien: "Parfois déséquilibrée; temps parfois irréaliste.",
    Insuffisant: "Mauvaise répartition compromettant l’apprentissage.",
  },
  "Présence d’évaluations formatives en classe": {
    Excellent: "Formatives régulières et bien conçues; respect du temps pratique.",
    Bien: "Présentes mais peu variées/espacées.",
    Insuffisant: "Peu ou pas d’évaluations formatives, ou mal alignées.",
  },
  "Présence de lectures ou de travaux hors classe": {
    Excellent: "Bien intégrés et utiles.",
    Bien: "Présents mais limités.",
    Insuffisant: "Absents ou sans lien clair.",
  },
  "Emploi d’approches issues de la pédagogie active": {
    Excellent: "Utilisation variée et cohérente de méthodes actives.",
    Bien: "Utilisation occasionnelle; engagement partiel.",
    Insuffisant: "Peu ou pas de méthodes actives.",
  },
  "Approches adaptées à la matière du cours": {
    Excellent: "Parfaitement alignées avec la matière.",
    Bien: "Généralement adaptées, parfois moyennement pertinentes.",
    Insuffisant: "Peu adaptées ou trop générales.",
  },
  "Alignement des stratégies d’enseignement avec le niveau taxonomique prescrit": {
    Excellent: "Parfaitement alignées.",
    Bien: "Alignement partiel.",
    Insuffisant: "Absence d’alignement.",
  },
  "Intégration des technologies éducatives": {
    Excellent: "Utilisation pertinente des outils.",
    Bien: "Utilisation limitée, impact modéré.",
    Insuffisant: "Peu ou pas d’utilisation.",
  },
  "Concordance de l’évaluation certificative avec le niveau taxonomique du cours": {
    Excellent: "Bien alignée avec le niveau taxonomique.",
    Bien: "Alignement partiel.",
    Insuffisant: "Non alignée, centrée sur des compétences de base.",
  },
  "Préparation à l’évaluation certificative": {
    Excellent: "Progression claire et graduelle.",
    Bien: "Progression adéquate avec quelques écarts.",
    Insuffisant: "Absence de progression claire.",
  },
  "Caractère authentique et complexe de l’évaluation certificative": {
    Excellent: "Situations authentiques et complexes.",
    Bien: "Complexité modérée, peu réaliste.",
    Insuffisant: "Simpliste, faible lien au contexte.",
  },
};

const GuideCell = ({ critere, selectedLevel }) => {
  const guide = GUIDE[critere] || {};
  return (
    <td className="guide-cell">
      <p className="guide-item" data-level="Excellent" data-active={selectedLevel === 'Excellent'}>
        <strong>Excellent :</strong> {guide.Excellent || ''}
      </p>
      <p className="guide-item" data-level="Bien" data-active={selectedLevel === 'Bien'}>
        <strong>Bien :</strong> {guide.Bien || ''}
      </p>
      <p className="guide-item" data-level="Insuffisant" data-active={selectedLevel === 'Insuffisant'}>
        <strong>Insuffisant :</strong> {guide.Insuffisant || ''}
      </p>
    </td>
  );
};

const CHECKBOX_SECTIONS = [
  { prefix: 'identification.', title: '1. Page d’identification (conformité avec plan-cadre*)' },
  { prefix: 'presentation.', title: '2. Présentation du cours (conformité avec plan-cadre*)' },
  { prefix: 'competences.', title: '3. Compétences et attitudes du cours' },
  { prefix: 'responsabilites.', title: '4. Responsabilités de l’étudiant' },
  { prefix: 'calendrier.', title: '5. Calendrier des activités d’apprentissage, d’enseignement et d’évaluation' },
  { prefix: 'evaluation_apprentissages.', title: '6. Évaluation des apprentissages' },
  { prefix: 'politiques.', title: '7. Politiques' },
  { prefix: 'ressources.', title: '8. Ressources du cours' },
  { prefix: 'epreuve.', title: '9. Épreuve certificative' },
];

const SectionHeader = ({ title, checked, partial, onChange, onClear }) => {
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = partial;
    }
  }, [partial]);

  return (
    <div className="section-header">
      <h3>{title}</h3>
      <div className="section-actions">
        <label className={`section-toggle${partial ? ' is-partial' : ''}`}>
          <input ref={checkboxRef} type="checkbox" checked={checked} onChange={onChange} />
          <span>{partial ? 'Section partiellement conforme' : 'Tout cocher conforme'}</span>
        </label>
        <button type="button" className="section-clear-btn" onClick={onClear}>
          Tout décocher
        </button>
      </div>
    </div>
  );
};

export default function ValidationInterface({
  plan,
  onStatusChange,
  statusUpdating,
  onSaveGrid,
  onOpenResult,
  initialPayload,
  loadingPayload,
  verifierName,
  validationRole = 'chef',
  saveButtonLabel,
  headerTitle,
  headerSubtitle,
  hideResultButton = false,
}) {
  const [levels, setLevels] = useState({});
  const [sectionChecks, setSectionChecks] = useState({});
  const { notice, showNotice, clearNotice } = useNotice();
  const formRef = useRef(null);
  const sectionRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const section = sectionRef.current;
      if (!section) return;
      const rect = section.getBoundingClientRect();
      setShowScrollTop(rect.top < -200);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const roleConfig = (() => {
    if (validationRole === 'conseiller') {
      return {
        title: headerTitle || 'Interface de Validation Pédagogique',
        subtitle: headerSubtitle || 'Sélectionnez un plan soumis par un chef d’équipe dans la file pour compléter la validation pédagogique.',
        saveLabel: saveButtonLabel || 'Enregistrer la validation pédagogique',
        requiredFieldName: 'conformite.conseiller_pedagogique',
        requiredFieldLabel: 'la conformité pédagogique du plan',
        approveStatus: 'validé',
        rejectStatus: 'non validé',
        approveButtonLabel: 'Valider',
        rejectButtonLabel: 'Non valider',
        showDirectionComment: false,
      };
    }

    if (validationRole === 'directeur') {
      return {
        title: headerTitle || 'Interface de Validation de la Direction Adjointe',
        subtitle: headerSubtitle || 'Sélectionnez un plan dans la file pour compléter l’approbation finale.',
        saveLabel: saveButtonLabel || 'Enregistrer la décision de la direction',
        requiredFieldName: 'conformite.directeur_adjoint',
        requiredFieldLabel: 'la décision de la direction adjointe',
        approveStatus: 'approuvé',
        rejectStatus: 'non approuvé',
        approveButtonLabel: 'Approuver',
        rejectButtonLabel: 'Refuser',
        showDirectionComment: true,
      };
    }

    return {
      title: headerTitle || 'Interface de Validation du Plan de Cours',
      subtitle: headerSubtitle || 'Sélectionnez un plan dans la queue.',
      saveLabel: saveButtonLabel || '✈️ Soumettre la révision',
      requiredFieldName: 'conformite.chef_equipe',
      requiredFieldLabel: 'la conformité du plan de cours',
      approveStatus: 'validé',
      rejectStatus: 'non validé',
      approveButtonLabel: 'Valider',
      rejectButtonLabel: 'Non valider',
      showDirectionComment: false,
    };
  })();

  const getSectionCheckboxes = (prefix) => {
    const form = formRef.current;
    if (!form) return [];

    return Array.from(form.querySelectorAll(`input[type="checkbox"][name^="${prefix}"]`)).filter((checkbox) => !checkbox.disabled);
  };

  const getSectionState = (prefix) => {
    const checkboxes = getSectionCheckboxes(prefix);
    const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length;
    const total = checkboxes.length;

    return {
      checked: total > 0 && checkedCount === total,
      partial: checkedCount > 0 && checkedCount < total,
    };
  };

  const syncSectionState = (prefix) => {
    const nextState = getSectionState(prefix);
    setSectionChecks((prev) => ({
      ...prev,
      [prefix]: nextState,
    }));
  };

  // Réinitialiser complètement le formulaire quand on change de plan
  useEffect(() => {
    if (!formRef.current) return;
    
    const form = formRef.current;
    
    // Vider tous les champs
    form.reset();
    
    // Réinitialiser tous les radio buttons
    form.querySelectorAll('input[type="radio"]').forEach(radio => {
      radio.checked = false;
    });
    
    // Réinitialiser tous les checkboxes
    form.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.checked = false;
    });
    
    // Réinitialiser tous les selects
    form.querySelectorAll('select').forEach(select => {
      select.selectedIndex = 0;
    });
    
    // Vider levels
    setLevels({});
    setSectionChecks({});
  }, [plan?.id]);

  // Charger le payload initial dans le formulaire
  useEffect(() => {
    if (!initialPayload || !formRef.current) return;

    const form = formRef.current;
    
    // Fonction récursive pour remplir tous les champs
    const fillFields = (obj, prefix = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const fieldName = prefix ? `${prefix}.${key}` : key;
        
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          // Si c'est un objet, continuer récursivement
          fillFields(value, fieldName);
        } else {
          // Chercher le champ dans le formulaire
          const input = form.querySelector(`[name="${fieldName}"]`);
          if (input) {
            if (input.type === 'checkbox') {
              input.checked = !!value;
            } else if (input.type === 'radio') {
              if (input.value === value) {
                input.checked = true;
              }
            } else if (input.tagName === 'SELECT') {
              input.value = value || '';
            } else if (input.tagName === 'TEXTAREA') {
              input.value = value || '';
            } else {
              input.value = value || '';
            }
          }
        }
      });
    };
    
    // Remplir tous les champs du formulaire
    fillFields(initialPayload);
    
    // Remplir levels pour synchroniser les selects des critères
    const newLevels = {};
    
    // Parcourir rubric.eval_apprentissages
    if (initialPayload.rubric?.eval_apprentissages) {
      Object.entries(initialPayload.rubric.eval_apprentissages).forEach(([key, data]) => {
        if (data && data.level) {
          // Mapper le nom du champ au nom du critère affiché
          const critereMap = {
            'totalite_competences': 'Évaluation de la totalité des compétences du cours',
            'distribution_equilibree': 'Distribution équilibrée des évaluations sommatives',
            'evaluations_formatives': 'Présence d\'évaluations formatives en classe',
            'travaux_hors_classe': 'Présence de lectures ou de travaux hors classe'
          };
          if (critereMap[key]) {
            newLevels[critereMap[key]] = data.level;
          }
        }
      });
    }
    
    // Parcourir rubric.strategies
    if (initialPayload.rubric?.strategies) {
      Object.entries(initialPayload.rubric.strategies).forEach(([key, data]) => {
        if (data && data.level) {
          const critereMap = {
            'pedagogie_active': 'Emploi d’approches issues de la pédagogie active',
            'adaptation_discipline': 'Approches adaptées à la matière du cours',
            'alignement_taxonomique': 'Alignement des stratégies d’enseignement avec le niveau taxonomique prescrit',
            'techno_educatives': 'Intégration des technologies éducatives'
          };
          if (critereMap[key]) {
            newLevels[critereMap[key]] = data.level;
          }
        }
      });
    }

    // Parcourir rubric.calendrier
    if (initialPayload.rubric?.calendrier) {
      Object.entries(initialPayload.rubric.calendrier).forEach(([key, data]) => {
        if (data && data.level) {
          const critereMap = {
            'formatives_classe': 'Présence d’évaluations formatives en classe',
            'hors_classe': 'Présence de lectures ou de travaux hors classe'
          };
          if (critereMap[key]) {
            newLevels[critereMap[key]] = data.level;
          }
        }
      });
    }
    
    // Parcourir rubric.epreuve
    if (initialPayload.rubric?.epreuve) {
      Object.entries(initialPayload.rubric.epreuve).forEach(([key, data]) => {
        if (data && data.level) {
          const critereMap = {
            'concordance_taxonomique': 'Concordance de l’évaluation certificative avec le niveau taxonomique du cours',
            'preparation': 'Préparation à l’évaluation certificative',
            'authentique_complexe': 'Caractère authentique et complexe de l’évaluation certificative'
          };
          if (critereMap[key]) {
            newLevels[critereMap[key]] = data.level;
          }
        }
      });
    }
    
    setLevels(newLevels);

    const nextSectionChecks = CHECKBOX_SECTIONS.reduce((accumulator, section) => {
      const checkboxes = Array.from(form.querySelectorAll(`input[type="checkbox"][name^="${section.prefix}"]`)).filter((checkbox) => !checkbox.disabled);
      const checkedCount = checkboxes.filter((checkbox) => checkbox.checked).length;
      accumulator[section.prefix] = {
        checked: checkboxes.length > 0 && checkedCount === checkboxes.length,
        partial: checkedCount > 0 && checkedCount < checkboxes.length,
      };
      return accumulator;
    }, {});

    setSectionChecks(nextSectionChecks);
  }, [initialPayload]);

  useEffect(() => {
    if (!plan || !formRef.current || loadingPayload) return;

    const form = formRef.current;
    const numeroCoursInput = form.querySelector('[name="infos.numero_cours"]');
    const verificateurInput = form.querySelector('[name="infos.verificateur"]');
    const dateInput = form.querySelector('[name="infos.date"]');

    if (numeroCoursInput && !numeroCoursInput.value?.trim() && plan.course && plan.course !== 'N/A') {
      numeroCoursInput.value = plan.course;
    }

    if (verificateurInput && !verificateurInput.value?.trim() && verifierName?.trim()) {
      verificateurInput.value = verifierName.trim();
    }

    if (dateInput && !dateInput.value) {
      dateInput.value = getTodayIsoDate();
    }
  }, [plan, verifierName, initialPayload, loadingPayload]);

  const handleLevelChange = (critere) => (event) => {
    setLevels((prev) => ({ ...prev, [critere]: event.target.value }));
  };

  const handleSectionToggle = (prefix) => (event) => {
    const nextChecked = event.target.checked;
    getSectionCheckboxes(prefix).forEach((checkbox) => {
      checkbox.checked = nextChecked;
    });
    setSectionChecks((prev) => ({
      ...prev,
      [prefix]: { checked: nextChecked, partial: false },
    }));
  };

  const handleSectionClear = (prefix) => () => {
    getSectionCheckboxes(prefix).forEach((checkbox) => {
      checkbox.checked = false;
    });
    setSectionChecks((prev) => ({
      ...prev,
      [prefix]: { checked: false, partial: false },
    }));
  };

  const handleFormChange = (event) => {
    const { target } = event;
    if (target.type !== 'checkbox' || !target.name) {
      return;
    }

    const section = CHECKBOX_SECTIONS.find((item) => target.name.startsWith(item.prefix));
    if (section) {
      syncSectionState(section.prefix);
    }
  };

  const clearValidationMessages = () => {
    const form = formRef.current;
    if (!form) return;

    form.querySelectorAll('input, textarea, select').forEach((field) => {
      field.setCustomValidity('');
    });
  };

  const getFieldLabel = (field, fallbackName) => {
    const tableRow = field?.closest('tr');
    const firstCell = tableRow?.querySelector('td');
    const rowLabel = firstCell?.querySelector('strong')?.textContent?.trim() || firstCell?.childNodes?.[0]?.textContent?.trim();
    if (rowLabel) {
      return rowLabel;
    }

    const label = field?.closest('label');
    const labelText = label?.childNodes?.[0]?.textContent?.trim();
    if (labelText) {
      return labelText;
    }

    return fallbackName;
  };

  const reportFieldError = (field, message) => {
    if (!field) return false;
    field.setCustomValidity(message);
    field.reportValidity();
    field.focus();
    return false;
  };

  const validateFormRules = () => {
    const form = formRef.current;
    if (!form) return false;

    clearValidationMessages();

    const generalRequiredFields = [
      { name: 'infos.numero_cours', label: 'le titre du cours' },
      { name: 'infos.verificateur', label: 'le nom du vérificateur' },
      { name: 'infos.date', label: 'la date de validation' },
    ];

    for (const item of generalRequiredFields) {
      const field = form.querySelector(`[name="${item.name}"]`);
      if (field && !field.value.trim()) {
        return reportFieldError(field, `Vous devez remplir ${item.label}.`);
      }
    }

    if (!validateRequiredConformite()) {
      return false;
    }

    const checkboxes = Array.from(form.querySelectorAll('input[type="checkbox"][name]'));
    for (const checkbox of checkboxes) {
      if (checkbox.disabled) continue;
      const commentField = form.querySelector(`[name="${checkbox.name}_comment"]`);
      if (!checkbox.checked && commentField && !commentField.value.trim()) {
        const fieldLabel = getFieldLabel(checkbox, checkbox.name);
        return reportFieldError(commentField, `Un commentaire est obligatoire pour « ${fieldLabel} » lorsque l'élément n'est pas conforme.`);
      }
    }

    const selects = Array.from(form.querySelectorAll('select[name]'));
    for (const select of selects) {
      if (select.disabled) continue;
      if (!select.value.trim()) {
        const fieldLabel = getFieldLabel(select, select.name);
        return reportFieldError(select, `Vous devez choisir une valeur pour « ${fieldLabel} ».`);
      }
    }

    return true;
  };

  const formToPayload = () => {
    const form = formRef.current;
    if (!form) return {};
    const data = JSON.parse(JSON.stringify(initialPayload || {}));
    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      const path = key.split('.');
      let cur = data;
      for (let i = 0; i < path.length; i += 1) {
        const part = path[i];
        const isLast = i === path.length - 1;
        if (!isLast) {
          if (!cur[part]) cur[part] = {};
          cur = cur[part];
        } else {
          cur[part] = value;
        }
      }
    }

    form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      if (!cb.name) return;
      const path = cb.name.split('.');
      let cur = data;
      for (let i = 0; i < path.length - 1; i += 1) {
        if (!cur[path[i]]) cur[path[i]] = {};
        cur = cur[path[i]];
      }
      if (!(path[path.length - 1] in cur)) {
        cur[path[path.length - 1]] = false;
      }
    });

    const radioGroups = new Set();
    form.querySelectorAll('input[type="radio"]').forEach((radio) => {
      if (radio.name) radioGroups.add(radio.name);
    });
    radioGroups.forEach((name) => {
      const path = name.split('.');
      let cur = data;
      for (let i = 0; i < path.length - 1; i += 1) {
        if (!cur[path[i]]) cur[path[i]] = {};
        cur = cur[path[i]];
      }
      if (!(path[path.length - 1] in cur)) {
        cur[path[path.length - 1]] = null;
      }
    });

    return data;
  };

  const validateRequiredConformite = () => {
    const form = formRef.current;
    if (!form) return false;

    const selectedChoice = form.querySelector(`input[name="${roleConfig.requiredFieldName}"]:checked`);
    if (selectedChoice) {
      return true;
    }

    showNotice(`Vous devez obligatoirement selectionner ${roleConfig.requiredFieldLabel}: Oui ou Non.`, 'warning');
    return false;
  };

  const handleSave = () => {
    if (!plan) return;
    if (!validateFormRules()) return;
    const payload = formToPayload();
    onSaveGrid?.(payload);
  };

  const handleGenerateResult = async () => {
    if (!plan) return;
    if (!validateFormRules()) return;
    const payload = formToPayload();
    await onSaveGrid?.(payload);
    onOpenResult?.(plan);
  };

  const handleStatusAction = (statut) => {
    if (!validateFormRules()) return;
    onStatusChange?.(statut, formToPayload());
  };

  const scrollToTop = () => {
    const section = sectionRef.current;
    if (!section) return;
    const top = section.getBoundingClientRect().top + window.scrollY - 88;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  return (
    <section className="validation-interface" ref={sectionRef}>
      {showScrollTop && (
        <button
          type="button"
          className="btn-scroll-top"
          onClick={scrollToTop}
          title="Remonter au début du formulaire"
          aria-label="Remonter au début du formulaire"
        >
          ↑ Haut
        </button>
      )}
      <header className="validation-header">
        <div>
          <h3>{roleConfig.title}</h3>
          <p className="muted">
            {plan
              ? `Plan: ${plan.title} (${plan.course}) — Enseignant: ${plan.teacher} — Statut: ${plan.statusLabel}`
              : roleConfig.subtitle}
          </p>
        </div>
        <div className="validation-actions">
          <button className="btn-primary" type="button" onClick={handleSave} disabled={!plan || statusUpdating}>
            {roleConfig.saveLabel}
          </button>

          {!hideResultButton && (
            <button
              className="btn-generate-pdf"
              type="button"
              onClick={handleGenerateResult}
              disabled={!plan || statusUpdating}
            >
              📄 Générer la grille au format Word
            </button>
          )}
          <button
            className="btn-success"
            type="button"
            disabled={!plan || statusUpdating}
            onClick={() => handleStatusAction(roleConfig.approveStatus)}
          >
            {roleConfig.approveButtonLabel}
          </button>
          <button
            className="btn-danger"
            type="button"
            disabled={!plan || statusUpdating}
            onClick={() => handleStatusAction(roleConfig.rejectStatus)}
          >
            {roleConfig.rejectButtonLabel}
          </button>
        </div>
      </header>

      <form className="grid-form" ref={formRef} onChange={handleFormChange}>
        <h2>Informations générales</h2>
        <label>
          Titre du cours
          <input type="text" name="infos.numero_cours" placeholder="Titre du cours" />
        </label>
        <label>
          Vérificateur
          <input type="text" name="infos.verificateur" placeholder="Votre nom" />
        </label>
        <label>
          Date
          <input type="date" name="infos.date" />
        </label>

        <h2>Conformité</h2>
        {validationRole === 'chef' ? (
          <div className="radio-group">
            <p><strong>Conformité du plan de cours (Chef d’équipe)</strong></p>
            <label><input type="radio" name="conformite.chef_equipe" value="oui" required /> Oui</label>
            <label><input type="radio" name="conformite.chef_equipe" value="non" required /> Non</label>
          </div>
        ) : validationRole === 'directeur' ? (
          <>
            <div className="radio-group radio-group-readonly">
              <p><strong>Conformité du plan de cours (Chef d’équipe)</strong></p>
              <span className="muted">Visible en lecture seule pour la direction adjointe.</span>
              <label><input type="radio" name="conformite.chef_equipe" value="oui" disabled /> Oui</label>
              <label><input type="radio" name="conformite.chef_equipe" value="non" disabled /> Non</label>
            </div>
            <div className="radio-group radio-group-readonly">
              <p><strong>Conformité Chef d’équipe (Conseiller pédagogique)</strong></p>
              <span className="muted">Visible en lecture seule pour la direction adjointe.</span>
              <label><input type="radio" name="conformite.conseiller_pedagogique" value="oui" disabled /> Oui</label>
              <label><input type="radio" name="conformite.conseiller_pedagogique" value="non" disabled /> Non</label>
            </div>
            <div className="radio-group">
              <p><strong>Validation par échantillonnage (Directeur adjoint aux études)</strong></p>
              <label><input type="radio" name="conformite.directeur_adjoint" value="oui" required /> Oui</label>
              <label><input type="radio" name="conformite.directeur_adjoint" value="non" required /> Non</label>
            </div>
          </>
        ) : (
          <>
            <div className="radio-group radio-group-readonly">
              <p><strong>Conformité du plan de cours (Chef d’équipe)</strong></p>
              <span className="muted">Non applicable dans ce flux de validation des plans soumis par les chefs d’équipe.</span>
            </div>
            <div className="radio-group">
              <p><strong>Conformité Chef d’équipe (Conseiller pédagogique)</strong></p>
              <label><input type="radio" name="conformite.conseiller_pedagogique" value="oui" required /> Oui</label>
              <label><input type="radio" name="conformite.conseiller_pedagogique" value="non" required /> Non</label>
            </div>
          </>
        )}
        {validationRole !== 'directeur' && (
          <div className="radio-group radio-group-readonly">
            <p><strong>Validation par e&#769;chantillonnage (Directeur adjoint aux e&#769;tudes)</strong></p>
            <span className="muted">Visible en lecture seule pour le conseiller pe&#769;dagogique.</span>
            <label><input type="radio" name="conformite.directeur_adjoint" value="oui" disabled /> Oui</label>
            <label><input type="radio" name="conformite.directeur_adjoint" value="non" disabled /> Non</label>
          </div>
        )}
        <h2>Éléments du plan de cours</h2>
        <SectionHeader
          title="1. Page d’identification (conformité avec plan-cadre*)"
          checked={!!sectionChecks['identification.']?.checked}
          partial={!!sectionChecks['identification.']?.partial}
          onChange={handleSectionToggle('identification.')}
          onClear={handleSectionClear('identification.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Numéro du cours</strong><span className="hint">Vérifier si c’est le bon numéro de cours</span></td><td><input type="checkbox" name="identification.numero_cours" /></td><td><textarea name="identification.numero_cours_comment" /></td></tr>
            <tr><td><strong>Titre du cours</strong><span className="hint">Vérifier si c’est le bon titre du cours</span></td><td><input type="checkbox" name="identification.titre_cours" /></td><td><textarea name="identification.titre_cours_comment" /></td></tr>
            <tr><td>Programme</td><td><input type="checkbox" name="identification.programme" /></td><td><textarea name="identification.programme_comment" /></td></tr>
            <tr><td>Discipline</td><td><input type="checkbox" name="identification.discipline" /></td><td><textarea name="identification.discipline_comment" /></td></tr>
            <tr><td><strong>Pondération (T-L-P)</strong><span className="hint">Est-ce la même que celle indiquée dans la grille de cours ?</span></td><td><input type="checkbox" name="identification.ponderation" /></td><td><textarea name="identification.ponderation_comment" /></td></tr>
            <tr><td><strong>Préalables ou concomitants</strong><span className="hint">Vérifier dans la grille de cours ou demander à la CP</span></td><td><input type="checkbox" name="identification.prealables" /></td><td><textarea name="identification.prealables_comment" /></td></tr>
            <tr><td><strong>Cours substituts</strong><span className="hint">Vérifier dans la grille de cours ou demander à la CP</span></td><td><input type="checkbox" name="identification.substituts" /></td><td><textarea name="identification.substituts_comment" /></td></tr>
            <tr><td>Session</td><td><input type="checkbox" name="identification.session" /></td><td><textarea name="identification.session_comment" /></td></tr>
            <tr><td>Local</td><td><input type="checkbox" name="identification.local" /></td><td><textarea name="identification.local_comment" /></td></tr>
            <tr><td>Type de formation</td><td><input type="checkbox" name="identification.type_formation" /></td><td><textarea name="identification.type_formation_comment" /></td></tr>
            <tr><td>Charge de travail hebdomadaire</td><td><input type="checkbox" name="identification.charge_travail" /></td><td><textarea name="identification.charge_travail_comment" /></td></tr>
            <tr><td>Nom de l’enseignant.e</td><td><input type="checkbox" name="identification.nom_enseignant" /></td><td><textarea name="identification.nom_enseignant_comment" /></td></tr>
            <tr><td>Coordonnées de l’enseignant.e</td><td><input type="checkbox" name="identification.coord_enseignant" /></td><td><textarea name="identification.coord_enseignant_comment" /></td></tr>
            <tr><td>Disponibilités de l’enseignant.e</td><td><input type="checkbox" name="identification.disponibilites" /></td><td><textarea name="identification.disponibilites_comment" /></td></tr>
            <tr><td>Nom et coordonnées de l’API</td><td><input type="checkbox" name="identification.api" /></td><td><textarea name="identification.api_comment" /></td></tr>
            <tr><td>Phrase clé du département/de la discipline</td><td><input type="checkbox" name="identification.phrase_cle" /></td><td><textarea name="identification.phrase_cle_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="2. Présentation du cours (conformité avec plan-cadre*)"
          checked={!!sectionChecks['presentation.']?.checked}
          partial={!!sectionChecks['presentation.']?.partial}
          onChange={handleSectionToggle('presentation.')}
          onClear={handleSectionClear('presentation.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Description du cours</strong><span className="hint">Est-ce la description officielle du cours (plan-cadre*) ?</span></td><td><input type="checkbox" name="presentation.description" /></td><td><textarea name="presentation.description_comment" /></td></tr>
            <tr><td>Place du cours dans le programme</td><td><input type="checkbox" name="presentation.place_programme" /></td><td><textarea name="presentation.place_programme_comment" /></td></tr>
            <tr><td>Réinvestissement des compétences</td><td><input type="checkbox" name="presentation.reinvestissement" /></td><td><textarea name="presentation.reinvestissement_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="3. Compétences et attitudes du cours"
          checked={!!sectionChecks['competences.']?.checked}
          partial={!!sectionChecks['competences.']?.partial}
          onChange={handleSectionToggle('competences.')}
          onClear={handleSectionClear('competences.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Nom.s de la/des compétence.s</strong><span className="hint">Est-ce la bonne compétence (devis ministériel) ?</span></td><td><input type="checkbox" name="competences.noms" /></td><td><textarea name="competences.noms_comment" /></td></tr>
            <tr><td><strong>Élément.s de la/des compétence.s</strong><span className="hint">Sont-ce les bons éléments de compétence du devis ?</span></td><td><input type="checkbox" name="competences.elements" /></td><td><textarea name="competences.elements_comment" /></td></tr>
            <tr><td><strong>Nom.s de l’/des attitude.s visée.s (savoir-être)</strong><span className="hint">Attitudes visées pertinentes ?</span></td><td><input type="checkbox" name="competences.attitudes" /></td><td><textarea name="competences.attitudes_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="4. Responsabilités de l’étudiant"
          checked={!!sectionChecks['responsabilites.']?.checked}
          partial={!!sectionChecks['responsabilites.']?.partial}
          onChange={handleSectionToggle('responsabilites.')}
          onClear={handleSectionClear('responsabilites.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>Responsabilités de l’étudiant</td><td><input type="checkbox" name="responsabilites.etudiant" /></td><td><textarea name="responsabilites.etudiant_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="5. Calendrier des activités d’apprentissage, d’enseignement et d’évaluation"
          checked={!!sectionChecks['calendrier.']?.checked}
          partial={!!sectionChecks['calendrier.']?.partial}
          onChange={handleSectionToggle('calendrier.')}
          onClear={handleSectionClear('calendrier.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Dates des cours</strong><span className="hint">15 semaines ? jours fériés/journées pédagogiques pris en compte ?</span></td><td><input type="checkbox" name="calendrier.dates_cours" /></td><td><textarea name="calendrier.dates_cours_comment" /></td></tr>
            <tr><td>Contenus des cours (principaux thèmes abordés)</td><td><input type="checkbox" name="calendrier.contenus_cours" /></td><td><textarea name="calendrier.contenus_cours_comment" /></td></tr>
            <tr><td>Évaluations sommatives avec pondération</td><td><input type="checkbox" name="calendrier.evaluations_sommatives" /></td><td><textarea name="calendrier.evaluations_sommatives_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="6. Évaluation des apprentissages"
          checked={!!sectionChecks['evaluation_apprentissages.']?.checked}
          partial={!!sectionChecks['evaluation_apprentissages.']?.partial}
          onChange={handleSectionToggle('evaluation_apprentissages.')}
          onClear={handleSectionClear('evaluation_apprentissages.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td><strong>Type d’évaluation (examen, projet, présentation orale, etc.)</strong><span className="hint">Diversité des types ?</span></td><td><input type="checkbox" name="evaluation_apprentissages.types" /></td><td><textarea name="evaluation_apprentissages.types_comment" /></td></tr>
            <tr><td><strong>Éléments de compétence</strong><span className="hint">Tous les éléments couverts durant la session ?</span></td><td><input type="checkbox" name="evaluation_apprentissages.elements_couverts" /></td><td><textarea name="evaluation_apprentissages.elements_couverts_comment" /></td></tr>
            <tr><td>Critères d’évaluation</td><td><input type="checkbox" name="evaluation_apprentissages.criteres" /></td><td><textarea name="evaluation_apprentissages.criteres_comment" /></td></tr>
            <tr><td>Échéancier</td><td><input type="checkbox" name="evaluation_apprentissages.echeancier" /></td><td><textarea name="evaluation_apprentissages.echeancier_comment" /></td></tr>
            <tr><td><strong>Pondération (100%)</strong><span className="hint">Est-ce que le total fait 100% ?</span></td><td><input type="checkbox" name="evaluation_apprentissages.ponderation_100" /></td><td><textarea name="evaluation_apprentissages.ponderation_100_comment" /></td></tr>
            <tr><td>L’épreuve certificative compte minimum pour 40%</td><td><input type="checkbox" name="evaluation_apprentissages.certificative_40" /></td><td><textarea name="evaluation_apprentissages.certificative_40_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="7. Politiques"
          checked={!!sectionChecks['politiques.']?.checked}
          partial={!!sectionChecks['politiques.']?.partial}
          onChange={handleSectionToggle('politiques.')}
          onClear={handleSectionClear('politiques.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>PIEA abrégée présente dans le gabarit</td><td><input type="checkbox" name="politiques.piea_abregee" /></td><td><textarea name="politiques.piea_abregee_comment" /></td></tr>
            <tr><td>Deux évaluations sommatives en plus de la certificative</td><td><input type="checkbox" name="politiques.deux_sommatives" /></td><td><textarea name="politiques.deux_sommatives_comment" /></td></tr>
            <tr><td>Politiques de l’Institut Teccart</td><td><input type="checkbox" name="politiques.teccart_harcelement" /></td><td><textarea name="politiques.teccart_harcelement_comment" /></td></tr>
            <tr><td>Décision application art. 4.9.2 (IA) de la PIEA</td><td><input type="checkbox" name="politiques.article_492_ia" /></td><td><textarea name="politiques.article_492_ia_comment" /></td></tr>
            <tr><td>Règles du cours</td><td><input type="checkbox" name="politiques.regles_du_cours" /></td><td><textarea name="politiques.regles_du_cours_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="8. Ressources du cours"
          checked={!!sectionChecks['ressources.']?.checked}
          partial={!!sectionChecks['ressources.']?.partial}
          onChange={handleSectionToggle('ressources.')}
          onClear={handleSectionClear('ressources.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>Liste du matériel obligatoire</td><td><input type="checkbox" name="ressources.materiel_obligatoire" /></td><td><textarea name="ressources.materiel_obligatoire_comment" /></td></tr>
            <tr><td>Médiagraphie</td><td><input type="checkbox" name="ressources.mediagraphie" /></td><td><textarea name="ressources.mediagraphie_comment" /></td></tr>
          </tbody>
        </table>

        <SectionHeader
          title="9. Épreuve certificative"
          checked={!!sectionChecks['epreuve.']?.checked}
          partial={!!sectionChecks['epreuve.']?.partial}
          onChange={handleSectionToggle('epreuve.')}
          onClear={handleSectionClear('epreuve.')}
        />
        <table>
          <thead><tr><th>Élément</th><th>Conforme</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr><td>Titre de l’évaluation</td><td><input type="checkbox" name="epreuve.titre" /></td><td><textarea name="epreuve.titre_comment" /></td></tr>
            <tr><td>Tâches</td><td><input type="checkbox" name="epreuve.taches" /></td><td><textarea name="epreuve.taches_comment" /></td></tr>
            <tr><td>Contexte de réalisation</td><td><input type="checkbox" name="epreuve.contexte" /></td><td><textarea name="epreuve.contexte_comment" /></td></tr>
            <tr><td>Pondération (≥ 40%)</td><td><input type="checkbox" name="epreuve.ponderation_40" /></td><td><textarea name="epreuve.ponderation_40_comment" /></td></tr>
            <tr><td>Semaine de l’évaluation</td><td><input type="checkbox" name="epreuve.semaine" /></td><td><textarea name="epreuve.semaine_comment" /></td></tr>
            <tr><td>Grille de correction (analytique et descriptive)</td><td><input type="checkbox" name="epreuve.grille_correction" /></td><td><textarea name="epreuve.grille_correction_comment" /></td></tr>
          </tbody>
        </table>

        <h2>Partie 2 – Appréciation qualitative</h2>
        <h3>1. Stratégies d’apprentissage</h3>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Emploi d’approches issues de la pédagogie active</td>
              <GuideCell
                critere="Emploi d’approches issues de la pédagogie active"
                selectedLevel={levels['Emploi d’approches issues de la pédagogie active']}
              />
              <td>
                <select
                  name="rubric.strategies.pedagogie_active.level"
                  value={levels['Emploi d’approches issues de la pédagogie active'] || ''}
                  onChange={handleLevelChange('Emploi d’approches issues de la pédagogie active')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.strategies.pedagogie_active.comment" /></td>
            </tr>
            <tr>
              <td>Approches adaptées à la matière du cours</td>
              <GuideCell
                critere="Approches adaptées à la matière du cours"
                selectedLevel={levels['Approches adaptées à la matière du cours']}
              />
              <td>
                <select
                  name="rubric.strategies.adaptation_discipline.level"
                  value={levels['Approches adaptées à la matière du cours'] || ''}
                  onChange={handleLevelChange('Approches adaptées à la matière du cours')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.strategies.adaptation_discipline.comment" /></td>
            </tr>
            <tr>
              <td>Alignement des stratégies d’enseignement avec le niveau taxonomique prescrit</td>
              <GuideCell
                critere="Alignement des stratégies d’enseignement avec le niveau taxonomique prescrit"
                selectedLevel={levels['Alignement des stratégies d’enseignement avec le niveau taxonomique prescrit']}
              />
              <td>
                <select
                  name="rubric.strategies.alignement_taxonomique.level"
                  value={levels['Alignement des stratégies d’enseignement avec le niveau taxonomique prescrit'] || ''}
                  onChange={handleLevelChange('Alignement des stratégies d’enseignement avec le niveau taxonomique prescrit')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.strategies.alignement_taxonomique.comment" /></td>
            </tr>
            <tr>
              <td>Intégration des technologies éducatives</td>
              <GuideCell
                critere="Intégration des technologies éducatives"
                selectedLevel={levels['Intégration des technologies éducatives']}
              />
              <td>
                <select
                  name="rubric.strategies.techno_educatives.level"
                  value={levels['Intégration des technologies éducatives'] || ''}
                  onChange={handleLevelChange('Intégration des technologies éducatives')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.strategies.techno_educatives.comment" /></td>
            </tr>
          </tbody>
        </table>

        <h3>2. Calendrier des activités d’apprentissage, d’enseignement et d’évaluation</h3>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Présence d’évaluations formatives en classe</td>
              <GuideCell
                critere="Présence d’évaluations formatives en classe"
                selectedLevel={levels['Présence d’évaluations formatives en classe']}
              />
              <td>
                <select
                  name="rubric.calendrier.formatives_classe.level"
                  value={levels['Présence d’évaluations formatives en classe'] || ''}
                  onChange={handleLevelChange('Présence d’évaluations formatives en classe')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.calendrier.formatives_classe.comment" /></td>
            </tr>
            <tr>
              <td>Présence de lectures ou de travaux hors classe</td>
              <GuideCell
                critere="Présence de lectures ou de travaux hors classe"
                selectedLevel={levels['Présence de lectures ou de travaux hors classe']}
              />
              <td>
                <select
                  name="rubric.calendrier.hors_classe.level"
                  value={levels['Présence de lectures ou de travaux hors classe'] || ''}
                  onChange={handleLevelChange('Présence de lectures ou de travaux hors classe')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.calendrier.hors_classe.comment" /></td>
            </tr>
          </tbody>
        </table>

        <h3>3. Évaluation des apprentissages</h3>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Évaluation de la totalité des compétences du cours</td>
              <GuideCell
                critere="Évaluation de la totalité des compétences du cours"
                selectedLevel={levels['Évaluation de la totalité des compétences du cours']}
              />
              <td>
                <select
                  name="rubric.eval_apprentissages.totalite_competences.level"
                  value={levels['Évaluation de la totalité des compétences du cours'] || ''}
                  onChange={handleLevelChange('Évaluation de la totalité des compétences du cours')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.eval_apprentissages.totalite_competences.comment" /></td>
            </tr>
            <tr>
              <td>Distribution équilibrée des évaluations sommatives</td>
              <GuideCell
                critere="Distribution équilibrée des évaluations sommatives"
                selectedLevel={levels['Distribution équilibrée des évaluations sommatives']}
              />
              <td>
                <select
                  name="rubric.eval_apprentissages.distribution_equilibree.level"
                  value={levels['Distribution équilibrée des évaluations sommatives'] || ''}
                  onChange={handleLevelChange('Distribution équilibrée des évaluations sommatives')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.eval_apprentissages.distribution_equilibree.comment" /></td>
            </tr>
          </tbody>
        </table>

        <h3>4. Épreuve certificative (EC) ou Épreuve synthèse de programme (ESP)</h3>
        <p className="hint">Si celle-ci n’est pas déjà donnée dans le plan-cadre*</p>
        <table className="rubric">
          <colgroup>
            <col className="critere" />
            <col className="guide" />
            <col className="choix" />
            <col className="comment" />
          </colgroup>
          <thead><tr><th>Critère</th><th>Guide</th><th>Choix</th><th>Commentaires</th></tr></thead>
          <tbody>
            <tr>
              <td>Concordance de l’évaluation certificative avec le niveau taxonomique du cours</td>
              <GuideCell
                critere="Concordance de l’évaluation certificative avec le niveau taxonomique du cours"
                selectedLevel={levels['Concordance de l’évaluation certificative avec le niveau taxonomique du cours']}
              />
              <td>
                <select
                  name="rubric.epreuve.concordance_taxonomique.level"
                  value={levels['Concordance de l’évaluation certificative avec le niveau taxonomique du cours'] || ''}
                  onChange={handleLevelChange('Concordance de l’évaluation certificative avec le niveau taxonomique du cours')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.epreuve.concordance_taxonomique.comment" /></td>
            </tr>
            <tr>
              <td>Préparation à l’évaluation certificative</td>
              <GuideCell
                critere="Préparation à l’évaluation certificative"
                selectedLevel={levels['Préparation à l’évaluation certificative']}
              />
              <td>
                <select
                  name="rubric.epreuve.preparation.level"
                  value={levels['Préparation à l’évaluation certificative'] || ''}
                  onChange={handleLevelChange('Préparation à l’évaluation certificative')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.epreuve.preparation.comment" /></td>
            </tr>
            <tr>
              <td>Caractère authentique et complexe de l’évaluation certificative</td>
              <GuideCell
                critere="Caractère authentique et complexe de l’évaluation certificative"
                selectedLevel={levels['Caractère authentique et complexe de l’évaluation certificative']}
              />
              <td>
                <select
                  name="rubric.epreuve.authentique_complexe.level"
                  value={levels['Caractère authentique et complexe de l’évaluation certificative'] || ''}
                  onChange={handleLevelChange('Caractère authentique et complexe de l’évaluation certificative')}
                >
                  <option value="">-- Choisir --</option>
                  <option>Excellent</option>
                  <option>Bien</option>
                  <option>Insuffisant</option>
                </select>
              </td>
              <td><textarea name="rubric.epreuve.authentique_complexe.comment" /></td>
            </tr>
          </tbody>
        </table>

        <div className="result-actions">
          <button
            className="btn-generate-pdf"
            type="button"
            onClick={handleGenerateResult}
            disabled={!plan || statusUpdating}
          >
            📄 Générer la grille au format Word
          </button>
        </div>
      </form>
      <NoticeToast notice={notice} onClose={clearNotice} />
    </section>
  );
}
